export type CanonicalIdSource = {
  id?: unknown;
  _id?: unknown;
  userId?: unknown;
  customerId?: unknown;
  driverId?: unknown;
  orderId?: unknown;
  businessOrderId?: unknown;
  user?: unknown;
  customer?: unknown;
  driver?: unknown;
};

export type CanonicalPrincipal = {
  id: string;
  role?: unknown;
  customerId?: string;
  driverId?: string;
};

const normalizeIdValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof (obj as { toHexString?: () => string }).toHexString === 'function') {
      return (obj as { toHexString: () => string }).toHexString();
    }
    const candidateId = obj._id ?? obj.id ?? obj.userId;
    if (candidateId !== undefined && candidateId !== null) {
      return normalizeIdValue(candidateId);
    }
    return null;
  }
  const normalized = String(value).trim();
  if (normalized === '[object Object]' || normalized.length === 0) return null;
  return normalized;
};

export const resolveCanonicalId = (...candidates: unknown[]): string | null => {
  for (const candidate of candidates) {
    const resolved = normalizeIdValue(candidate);
    if (resolved) return resolved;
  }
  return null;
};

export const resolveCanonicalOrderId = (source: CanonicalIdSource | null | undefined): string | null => {
  if (!source || typeof source !== 'object') return null;
  return resolveCanonicalId(source.orderId, source.businessOrderId, source.id, source._id);
};

export const resolveCanonicalCustomerId = (source: CanonicalIdSource | null | undefined): string | null => {
  if (!source || typeof source !== 'object') return null;
  return resolveCanonicalId(source.customerId, source.userId, source.user, source.customer);
};

export const resolveCanonicalDriverId = (source: CanonicalIdSource | null | undefined): string | null => {
  if (!source || typeof source !== 'object') return null;
  return resolveCanonicalId(source.driverId, source.userId, source.user, source.driver);
};

export const resolveCanonicalPrincipal = (
  source: CanonicalIdSource | null | undefined,
  role?: unknown
): CanonicalPrincipal | null => {
  if (!source || typeof source !== 'object') return null;
  const id = resolveCanonicalId(source.id, source._id, source.userId, source.customerId, source.driverId);
  if (!id) return null;
  return {
    id,
    role,
    customerId: resolveCanonicalCustomerId(source) ?? id,
    driverId: resolveCanonicalDriverId(source) ?? id
  };
};
